import { type Access } from '@atlaskit/linking-types';
import {
  CloudId,
  JiraIssueRef,
  JiraServer,
  JiraServerId,
  ResolvedJiraIssueRef,
} from '@databases/types/confluence';

import { AsyncOperation, AsyncOperationStatus, validateStatus } from '../redux';

import { JiraState, getKey } from './slice';

type ContextState = {
  jira: JiraState;
};

/**
 * Returns the resolved Jira issue with the specified issue key from the store. If the issue is not loaded, an error is thrown.
 * @throws If the issue is not loaded, an error is thrown.
 */
export function selectJiraIssueRequired(
  state: ContextState,
  ref: JiraIssueRef
): ResolvedJiraIssueRef {
  validateStatus(selectJiraIssueMetadata(state, ref));

  const issue = selectJiraIssueOptional(state, ref);
  if (!issue) {
    throw new Error(`Jira issue ${ref.key} is not loaded.`);
  }
  return issue;
}

/**
 * Returns the resolved Jira issue with the specified issue key from the store. If the issue is not loaded, undefined is returned.
 * If there was an error loading the issue, undefined is returned.
 */
export function selectJiraIssueOptional(
  state: ContextState,
  ref: JiraIssueRef
): ResolvedJiraIssueRef | undefined {
  if (ref == null) {
    return undefined;
  }

  if (selectJiraIssueMetadata(state, ref)?.status === AsyncOperationStatus.ERROR) {
    return undefined;
  }

  const key = getKey(ref);
  return state.jira.issues[key]?.[ref.key];
}

/**
 * Returns the metadata of the loadJiraIssue action for the given issue key,
 * which includes the load status and its access level.
 */
export function selectJiraIssueMetadata(
  state: ContextState,
  ref: JiraIssueRef | undefined
): AsyncOperation<Access> | undefined {
  if (ref == null) {
    return undefined;
  }

  const key = getKey(ref);
  return state.jira.issuesMetadata[key]?.[ref.key];
}

/**
 * Returns the issue columns that should be fetched for all future requests.
 */
export function selectJiraIssueColumns(
  state: ContextState,
  ref: { cloudId: CloudId; serverId: JiraServerId }
): string[] {
  const key = getKey(ref);
  return state.jira.columns[key] ?? [];
}

export function selectJiraServers(state: ContextState): JiraServer[] | undefined {
  return state.jira.jiraServers;
}

export function selectJiraServersStatus(
  state: ContextState
): AsyncOperation<void> | undefined {
  return state.jira.jiraServersStatus;
}
